// ============================================================================
// ACCOUNT CASES HOOK (ACCT-CASES-001)
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { accountCasesService } from '../../api/services/accountCasesService';
import type { ID } from '../../api/types';
import type {
  AccountCases,
  ActionableAccountRow,
  ActionableAccountSearchRequest,
} from '../../api/types/accountCases.types';

const emptyPagination = {
  currentPage: 1, pageSize: 25, totalPages: 0, totalItems: 0, hasNextPage: false, hasPreviousPage: false,
};

export const useActionableAccounts = (params?: ActionableAccountSearchRequest) => {
  const [data, setData] = useState<ActionableAccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState(emptyPagination);

  const key = JSON.stringify(params);
  const fetch = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await accountCasesService.getActionable(params);
      setData(res.data); setPagination(res.pagination);
    } catch (err) { setError(err as Error); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, pagination, refetch: fetch };
};

export const useAccountCases = (accountId: ID | undefined) => {
  const [data, setData] = useState<AccountCases | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await accountCasesService.getByAccount(accountId);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, refetch: fetch };
};
